'use client';

import React, { useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import { m, AnimatePresence } from 'framer-motion';

export interface DrawerProps {
  children: React.ReactNode;
  open: boolean;
  onClose: () => void;
  anchor?: 'left' | 'right' | 'top' | 'bottom';
  hideBackdrop?: boolean;
  className?: string;
  backdropClassName?: string;
  disableBackdropClick?: boolean;
  disableScrollLock?: boolean;
  style?: React.CSSProperties;
  onExited?: () => void;
  /**
   * If true, the drawer shows instantly without mount (enter) animation.
   * Exit animation still plays.
   */
  mountInstant?: boolean;
}

export const Drawer: React.FC<DrawerProps> = ({
  children,
  open,
  onClose,
  anchor = 'left',
  hideBackdrop = false,
  className = '',
  backdropClassName = '',
  disableBackdropClick = false,
  disableScrollLock = false,
  style,
  onExited,
  mountInstant = false,
}) => {
  useEffect(() => {
    if (!disableScrollLock) {
      document.body.style.overflow = open ? 'hidden' : '';
      return () => {
        document.body.style.overflow = '';
      };
    }
    return undefined;
  }, [open, disableScrollLock]);

  const handleBackdropClick = () => {
    if (!disableBackdropClick) {
      onClose();
    }
  };

  const getPositionClasses = () => {
    switch (anchor) {
      case 'left':
        return {
          position: 'fixed inset-y-0 left-0',
          size: 'w-[320px] h-dvh',
          variants: { hidden: { x: '-100%' }, visible: { x: 0 }, exit: { x: '-100%' } },
        };
      case 'right':
        return {
          position: 'fixed inset-y-0 right-0',
          size: 'w-[320px] h-dvh',
          variants: { hidden: { x: '100%' }, visible: { x: 0 }, exit: { x: '100%' } },
        };
      case 'top':
        return {
          position: 'fixed inset-x-0 top-0',
          size: 'h-[320px] w-full',
          variants: { hidden: { y: '-100%' }, visible: { y: 0 }, exit: { y: '-100%' } },
        };
      case 'bottom':
        return {
          position: 'fixed inset-x-0 bottom-0',
          size: 'h-[320px] w-full',
          variants: { hidden: { y: '100%' }, visible: { y: 0 }, exit: { y: '100%' } },
        };
      default:
        return {
          position: 'fixed inset-y-0 left-0',
          size: 'w-[320px] h-dvh',
          variants: { hidden: { x: '-100%' }, visible: { x: 0 }, exit: { x: '-100%' } },
        };
    }
  };

  const { position, size, variants } = getPositionClasses();

  return (
    <AnimatePresence onExitComplete={onExited}>
      {open && (
        <>
          {!hideBackdrop && (
            <m.div
              key="backdrop"
              initial={mountInstant ? false : { opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className={twMerge(
                'fixed inset-0 z-40 h-[110vh] -right-1 -top-1 -bottom-1 -left-1 bg-black transition-opacity',
                backdropClassName
              )}
              onClick={handleBackdropClick}
              aria-hidden="true"
            />
          )}

          <m.div
            key="drawer"
            initial={mountInstant ? false : 'hidden'}
            animate="visible"
            exit="exit"
            variants={variants}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className={twMerge(position, size, 'z-50 bg-white shadow-xl', className)}
            role="dialog"
            aria-modal="true"
            style={style}
          >
            {children}
          </m.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default Drawer;
